import { useCallback, useMemo, useState } from "react";
import { Button, Divider, Group, Popover, Stack, Text } from "@mantine/core";
import { DatePickerInput, type DatesRangeValue } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { IconCalendar, IconChevronDown } from "@tabler/icons-react";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import {
  addCalendarDays,
  formatDateOnlyWithShortMonth,
  getLabTodayDateOnly,
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";
import { DateRangePresetRow } from "./DateRangePresetRow";

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

interface ReportDateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
}

type PresetKey =
  | "today"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "ytd"
  | "lastYear"
  | "custom";

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "today", label: "Today" },
  { key: "last7", label: "Last 7 Days" },
  { key: "last30", label: "Last 30 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "ytd", label: "YTD" },
  { key: "lastYear", label: "Last Year" },
];

function getPresetRange(
  key: PresetKey,
  labToday: DateOnlyString
): DateRange | null {
  const currentMonthStart = requireDateOnlyString(`${labToday.slice(0, 7)}-01`);
  switch (key) {
    case "today":
      return pickerRange(labToday, labToday);
    case "last7":
      return pickerRange(requireCalendarOffset(labToday, -6), labToday);
    case "last30":
      return pickerRange(requireCalendarOffset(labToday, -29), labToday);
    case "thisMonth":
      return pickerRange(currentMonthStart, labToday);
    case "lastMonth": {
      const previousMonthEnd = requireCalendarOffset(currentMonthStart, -1);
      const previousMonthStart = requireDateOnlyString(
        `${previousMonthEnd.slice(0, 7)}-01`
      );
      return pickerRange(previousMonthStart, previousMonthEnd);
    }
    case "ytd":
      return pickerRange(
        requireDateOnlyString(`${labToday.slice(0, 4)}-01-01`),
        labToday
      );
    case "lastYear": {
      const priorYear = Number(labToday.slice(0, 4)) - 1;
      return pickerRange(
        requireDateOnlyString(`${priorYear}-01-01`),
        requireDateOnlyString(`${priorYear}-12-31`)
      );
    }
    default:
      return null;
  }
}

export function getLastMonthRange(now: Date = new Date()): DateRange {
  const range = getPresetRange("lastMonth", getLabTodayDateOnly(now));
  if (!range) {
    throw new Error("lastMonth preset is missing");
  }
  return range;
}

function rangesMatch(a: DateRange, b: DateRange): boolean {
  return (
    pickerDateToDateOnly(a.startDate) === pickerDateToDateOnly(b.startDate) &&
    pickerDateToDateOnly(a.endDate) === pickerDateToDateOnly(b.endDate)
  );
}

function presetForValue(value: DateRange, labToday: DateOnlyString): PresetKey {
  const match = PRESETS.find((preset) => {
    const range = getPresetRange(preset.key, labToday);
    return range !== null && rangesMatch(range, value);
  });
  return match?.key ?? "custom";
}

function formatRangeLabel(value: DateRange): string {
  const start = pickerDateToDateOnly(value.startDate);
  const end = pickerDateToDateOnly(value.endDate);
  if (start === end) {
    return formatDateOnlyWithShortMonth(start);
  }
  if (start.slice(0, 4) === end.slice(0, 4)) {
    return `${formatDateOnlyWithShortMonth(start, {
      omitYear: true,
    })} – ${formatDateOnlyWithShortMonth(end)}`;
  }
  return `${formatDateOnlyWithShortMonth(
    start
  )} – ${formatDateOnlyWithShortMonth(end)}`;
}

export function ReportDateRangePicker({
  value,
  onChange,
}: ReportDateRangePickerProps) {
  const labToday = useLabTodayDateOnly();
  const [opened, { close, toggle }] = useDisclosure(false);
  const [tempRange, setTempRange] = useState<DatesRangeValue>([
    value.startDate,
    value.endDate,
  ]);

  const activePreset = useMemo(
    () => presetForValue(value, labToday),
    [labToday, value]
  );
  const presetLabel = useMemo(() => {
    const match = PRESETS.find((preset) => preset.key === activePreset);
    return match?.label ?? "Custom";
  }, [activePreset]);
  const rangeLabel = useMemo(() => formatRangeLabel(value), [value]);

  const handleToggle = useCallback(() => {
    if (!opened) {
      setTempRange([value.startDate, value.endDate]);
    }
    toggle();
  }, [opened, toggle, value]);

  const handlePresetSelect = useCallback(
    (key: string) => {
      const range = getPresetRange(key as PresetKey, labToday);
      if (range) {
        onChange(range);
        close();
      }
    },
    [close, labToday, onChange]
  );

  const handleCustomChange = useCallback(
    (dates: DatesRangeValue) => {
      setTempRange(dates);
      if (dates[0] && dates[1]) {
        const selectedStart = pickerDateToDateOnly(dates[0]);
        const selectedEnd = pickerDateToDateOnly(dates[1]);
        const start = selectedStart > labToday ? labToday : selectedStart;
        const end = selectedEnd > labToday ? labToday : selectedEnd;
        onChange(pickerRange(start, end));
      }
    },
    [labToday, onChange]
  );

  return (
    <Popover
      opened={opened}
      onChange={close}
      position="bottom-start"
      offset={6}
      withinPortal
      shadow="md"
      radius="md"
    >
      <Popover.Target>
        <Button
          variant="default"
          leftSection={<IconCalendar size={16} />}
          rightSection={<IconChevronDown size={14} />}
          onClick={handleToggle}
          styles={{
            inner: { justifyContent: "space-between" },
            label: { fontWeight: 500 },
          }}
        >
          <Group gap={6} wrap="nowrap">
            <Text size="sm" fw={500}>
              {presetLabel}
            </Text>
            <Text size="sm" c="dimmed">
              · {rangeLabel}
            </Text>
          </Group>
        </Button>
      </Popover.Target>
      <Popover.Dropdown p={6}>
        <Stack gap={2} w={260}>
          {PRESETS.map((preset) => (
            <DateRangePresetRow
              key={preset.key}
              label={preset.label}
              active={preset.key === activePreset}
              presetKey={preset.key}
              onSelect={handlePresetSelect}
            />
          ))}
          <Divider my={6} />
          <Text size="xs" fw={500} c="dimmed" px="xs" pb={4}>
            Custom range
          </Text>
          <DatePickerInput
            type="range"
            value={tempRange}
            onChange={handleCustomChange}
            placeholder="Pick date range"
            size="xs"
            maxDate={pickerDateAtStart(labToday)}
            popoverProps={{ withinPortal: false, position: "bottom" }}
          />
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

function pickerRange(start: DateOnlyString, end: DateOnlyString): DateRange {
  return {
    startDate: pickerDateAtStart(start),
    endDate: pickerDateAtEnd(end),
  };
}

function pickerDateAtStart(value: DateOnlyString): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
}

function pickerDateAtEnd(value: DateOnlyString): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999);
}

function pickerDateToDateOnly(value: Date | string): DateOnlyString {
  if (typeof value === "string") {
    return requireDateOnlyString(value, "Report date");
  }
  return requireDateOnlyString(
    `${String(value.getFullYear()).padStart(4, "0")}-${String(
      value.getMonth() + 1
    ).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`,
    "Report date"
  );
}

function requireCalendarOffset(
  value: DateOnlyString,
  days: number
): DateOnlyString {
  const result = addCalendarDays(value, days);
  if (!result)
    throw new Error("Report date preset is outside the supported range");
  return result;
}
